import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  Bot,
  Check,
  ChevronDown,
  ChevronRight,
  CircleDot,
  CornerDownRight,
  Crown,
  HelpCircle,
  Layers,
  LayoutGrid,
  Lightbulb,
  Download,
  Folder,
  FolderGit2,
  Globe,
  Laptop,
  Link2,
  Lock,
  Trash2,
  FolderOpen,
  FolderClosed,
  Loader2,
  MoreHorizontal,
  PanelLeftOpen,
  Pause,
  Pencil,
  Pin,
  PinOff,
  Play,
  Plus,
  RotateCcw,
  Search,
  Square,
  Ungroup,
  X,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EmptyState } from "@/components/ui/misc";
import { Hint } from "@/components/ui/tooltip";
import { AgentAvatar } from "@/components/agent-avatar";
import { AgentDialog } from "@/components/agent-dialog";
import { AgentComposer } from "@/components/agent-composer";
import { AgentTriggersDialog } from "@/components/agent-triggers-dialog";
import { NeedsYouPill, PendingQuestionsBar, QuestionAge } from "@/components/pending-question";
import { McpCountChip, McpToolsButton } from "@/components/mcp-tools";
import { FallbackPill } from "@/components/model-fallback";
import { PreviewsButton } from "@/components/previews";
import { DeployPausePill } from "@/components/deploy-pause";
import { AgentPausedBanner, AgentPausePill } from "@/components/agent-pause";
import { ForeignRowLine, ForeignWaitingBanner, PresenceDot } from "@/components/foreign-presence";
import { ForeignAgentDialog, PairingDialog } from "@/components/foreign-agents";
import { machineName, presenceLabel } from "@/lib/foreign-agents";
import { EmailDraftCard } from "@/components/email-draft";
import { cleanSelectionHtml, emailOf } from "@/lib/email";
import { useStopAgent } from "@/components/stop-agent-button";
import { AgentActionItems, type AgentAct } from "@/components/agent-actions-menu";
import { useLongPress } from "@/hooks/use-long-press";
import { useTouchLiftDrag } from "@/hooks/use-touch-drag";
import { FOLDER_RENAME_EVENT, useAgentFolders, useFolderActions } from "@/hooks/use-agent-folders";
import {
  dropTargetKey,
  folderDrop,
  groupAgentList,
  parseDropTarget,
  readCollapsedFolders,
  writeCollapsedFolders,
  type FolderDropTarget,
  type FolderGroup,
} from "@/lib/agent-folders";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Markdown } from "@/components/markdown";
import { agentHandles, MentionText } from "@/components/mention-text";
import { mentionCandidates } from "@/components/mention-autocomplete";
import { shortPath } from "@/components/project-select";
import { useImageViewer, VIEWER_ICON_BUTTON } from "@/components/image-viewer";
import { focusAgentMessage, takeAgentFocus, useAgentFocusRequest } from "@/lib/agent-focus";
import { api, ApiError } from "@/lib/api";
import { useCan } from "@/lib/auth";
import { copyFileLink, humanBytes } from "@/lib/files";
import { relativeTime } from "@/lib/format";
import { previewText } from "@/lib/preview-text";
import { useLiveConnected, useLiveEvent } from "@/lib/live";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { keepPrevious } from "@/lib/query/client";
import { qk } from "@/lib/query/keys";
import {
  flattenTranscript,
  olderCursor,
  TRANSCRIPT_PAGE,
  upsertTranscript,
  patchTranscriptAttachment,
  type TranscriptCursor,
  type TranscriptData,
  type TranscriptPage,
} from "@/lib/query/transcript";
import {
  agentStatusColor,
  AGENT_STATUS_LABELS,
  type AgentAttachment,
  type AgentFolder,
  type AgentMessage,
  type AgentOpenQuestion,
  type AgentTurn,
  type AgentWithContext,
  type Board,
  type BoardDetail,
  type Project,
  type User,
} from "@/lib/types";
import { callDetail, groupToolRuns, subagentLabel, summarizeTools, toolCallOf, type ToolCall } from "@/lib/tool-summary";
import { cn } from "@/lib/utils";
import { requestOpenBeside, setViewDrag } from "@/lib/window-intents";
import { agentNeedsYou } from "@/lib/waiting";
import { boardsOfProject } from "@/lib/linked-boards";
import { useWaiting } from "@/lib/waiting-context";
import { FileIcon, fileTypeLabel } from "@/components/file-chip";
import { useAutoSend, useVoiceSupport } from "@/lib/voice/settings";

/** Rows drag with a mouse only on a fine pointer; a finger drags through `useTouchLiftDrag`, as the board's cards do. */
const FINE_POINTER = typeof window !== "undefined" && window.matchMedia?.("(pointer: fine)").matches;

const selectAgents = (data: { agents: AgentWithContext[] }) => data.agents;
const NO_AGENTS: AgentWithContext[] = [];
const selectTurns = (data: { turns: AgentTurn[] }) => data.turns;
const NO_TURNS: AgentTurn[] = [];

/**
 * Every agent, from the cache. Kept current by the live feed: an `agent.updated`
 * or a new transcript row invalidates the list (debounced, since a working agent
 * emits several a second), and so does a revision that wrote an agent's rows.
 */
export function useAgents() {
  const client = useQueryClient();
  const query = useQuery({ queryKey: qk.agents(), queryFn: api.listAgents, select: selectAgents });
  const refresh = useCallback(() => client.invalidateQueries({ queryKey: qk.agents() }), [client]);
  return { agents: query.data ?? NO_AGENTS, loading: query.isPending, refresh };
}

/**
 * The agents as a chat app: a list on the left like WhatsApp's or Teams', each
 * row with its status and the last thing said, and the conversation on the right
 * as it happens. Narrower than 768px — the view's own width, so a half-width window
 * counts — it is one pane at a time, list then chat, with a back button, the way
 * those apps work on a phone.
 */
export function AgentsView({
  projects,
  users,
  now,
  selectedId,
  onSelect,
  onManageProjects,
  onOpenProject,
  onOpenTask,
  boards = [],
  onOpenBoard,
  newAgentRequest = 0,
  showTitle = true,
  pane = "both",
  activeId,
}: {
  /**
   * `both` is list and chat in one view (the tab layout). On the desktop they are
   * separate windows: `list` is the list alone, `chat` the selected chat alone.
   */
  pane?: "both" | "list" | "chat";
  /** The row marked open, when that is not `selectedId` — a list window's chat is in another window. */
  activeId?: string | null;
  projects: Project[];
  /** The live, non-archived boards: a chat links the ones in its agent's project. */
  boards?: BoardDetail[];
  onOpenBoard?: (boardId: string) => void;
  users: User[];
  now: number;
  selectedId: string | null;
  onSelect: (agentId: string | null) => void;
  onManageProjects: () => void;
  /** The ⋯ menus' "Open project": the Projects page with that project's sheet open. */
  onOpenProject?: (projectId: string) => void;
  onOpenTask: (taskId: string) => void;
  /** Bumped by the tab layout's top bar to open the new-agent dialog from outside. */
  newAgentRequest?: number;
  /** The list's own "Agents" + New row. Off with tabs, whose top bar already names the view and carries the +. */
  showTitle?: boolean;
}) {
  const { agents, loading, refresh } = useAgents();
  const canManage = useCan("admin");
  const [editing, setEditing] = useState<AgentWithContext | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selected = agents.find((agent) => agent.id === selectedId) ?? null;
  /** A list window whose chat is already in a window beside it: nothing to keep room for. */
  const listBesideChat = pane === "list" && Boolean(activeId);
  const folders = useAgentFolders();
  const folderActions = useFolderActions();
  // Collapsed folders are this device's, like the sidebar's state.
  const [collapsed, setCollapsed] = useState<Set<string>>(() => readCollapsedFolders());
  const setFolderOpen = useCallback((folderId: string, open: boolean) => {
    setCollapsed((previous) => {
      if (previous.has(folderId) !== open) return previous;
      const next = new Set(previous);
      if (open) next.delete(folderId);
      else next.add(folderId);
      writeCollapsedFolders(next);
      return next;
    });
  }, []);
  const [renaming, setRenaming] = useState<string | null>(null);
  useEffect(() => {
    const onRename = (event: Event) => {
      const folderId = (event as CustomEvent<string>).detail;
      setFolderOpen(folderId, true);
      setRenaming(folderId);
    };
    window.addEventListener(FOLDER_RENAME_EVENT, onRename);
    return () => window.removeEventListener(FOLDER_RENAME_EVENT, onRename);
  }, [setFolderOpen]);
  const [ungrouping, setUngrouping] = useState<AgentFolder | null>(null);
  // The server's order (`compareAgentsForList`) cut into its groups: the master, which
  // manages the rest, then what the user pinned, then each folder, then everyone loose.
  const { master, pinned, folders: folderGroups, loose } = useMemo(
    () => groupAgentList(agents, folders, query, collapsed),
    [agents, folders, query, collapsed],
  );

  /* ---- dragging an agent onto another, into a folder, or out of one ---- */
  const canArrange = canManage;
  const listRef = useRef<HTMLDivElement>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [liftedId, setLiftedId] = useState<string | null>(null);
  const [overKey, setOverKey] = useState<string | null>(null);
  const [menuRequest, setMenuRequest] = useState<MenuRequest | null>(null);
  const endDrag = () => {
    setDragId(null);
    setLiftedId(null);
    setOverKey(null);
  };
  /** The drop target under an element, if letting go there would change anything. */
  const targetAt = (element: Element | null, draggedId: string): FolderDropTarget | null => {
    const target = parseDropTarget(element?.closest<HTMLElement>("[data-drop]")?.dataset.drop);
    return folderDrop(draggedId, target, agents).kind === "none" ? null : target;
  };
  const dropOn = (draggedId: string, target: FolderDropTarget) => {
    const action = folderDrop(draggedId, target, agents);
    if (action.kind === "move") folderActions.moveTo(action.agentId, action.folderId);
    else if (action.kind === "create") {
      folderActions.createWith(action.agentIds.map((id) => agents.find((agent) => agent.id === id)!));
    }
  };
  useTouchLiftDrag<FolderDropTarget>(listRef, canArrange, {
    itemSelector: "[data-agent-drag]",
    idOf: (item) => item.dataset.agentDrag,
    dragOnLift: false,
    resolveTarget: (x, y, id) => targetAt(document.elementFromPoint(x, y), id),
    sameTarget: (a, b) => dropTargetKey(a) === dropTargetKey(b),
    scrollers: () => ({ vertical: listRef.current }),
    ghostTransform: "scale(1.03)",
    onLift: setLiftedId,
    onStart: (id) => {
      setDragId(id);
      setLiftedId(null);
    },
    onOver: (target) => setOverKey(dropTargetKey(target)),
    onDrop: (id, target) => {
      endDrag();
      dropOn(id, target);
    },
    onCancel: endDrag,
    onRelease: (agentId, point) => setMenuRequest({ agentId, ...point, nonce: Date.now() }),
  });
  /** A mouse's drag: native HTML5, delegated to the list so every row and header is a target. */
  const mouseDrop = {
    onDragOver: (event: React.DragEvent) => {
      if (!dragId) return;
      const target = targetAt(event.target as Element, dragId);
      if (target) {
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
      }
      const key = dropTargetKey(target);
      if (key !== overKey) setOverKey(key);
    },
    onDrop: (event: React.DragEvent) => {
      if (!dragId) return;
      event.preventDefault();
      const target = targetAt(event.target as Element, dragId);
      const id = dragId;
      endDrag();
      if (target) dropOn(id, target);
    },
  };
  const dragging = dragId ? agents.find((agent) => agent.id === dragId) ?? null : null;

  const togglePin = async (agent: AgentWithContext) => {
    try {
      await api.updateAgent(agent.id, { pinned: !agent.pinned });
      void refresh();
    } catch (cause) {
      toast.error(cause instanceof ApiError ? cause.message : `Could not ${agent.pinned ? "unpin" : "pin"} ${agent.name}`);
    }
  };
  /** A row menu's action: the list refreshes after it, a failure is a toast (the row has no room for more). */
  const rowAct: AgentAct = async (label, fn) => {
    try {
      await fn();
      void refresh();
    } catch (cause) {
      toast.error(cause instanceof ApiError ? cause.message : `Could not ${label}`);
    }
  };
  const row = (agent: AgentWithContext) => (
    <AgentRow
      key={agent.id}
      agent={agent}
      now={now}
      active={agent.id === (activeId !== undefined ? activeId : selectedId)}
      arrange={
        canArrange && agent.kind !== "master"
          ? {
              lifted: liftedId === agent.id,
              dragging: dragId === agent.id,
              dropTarget: overKey === `agent:${agent.id}`,
              menuRequest: menuRequest?.agentId === agent.id ? menuRequest : null,
              onDragStart: (event) => {
                event.dataTransfer.setData("text/plain", agent.handle);
                // Also a view: dropped on the desktop workspace, the row opens its chat as a window.
                setViewDrag(event.dataTransfer, { kind: "agents", agentId: agent.id });
                setDragId(agent.id);
              },
              onDragEnd: endDrag,
            }
          : undefined
      }
      onSelect={() => onSelect(agent.id)}
      // A list window of the desktop workspace: the row's menu offers what Ctrl/Cmd-click does, for a finger.
      onOpenBeside={
        pane === "list"
          ? () => {
              requestOpenBeside();
              onSelect(agent.id);
            }
          : undefined
      }
      onOpenQuestion={(messageId) => openAt(agent.id, messageId)}
      onTogglePin={canManage && agent.kind !== "master" ? () => void togglePin(agent) : undefined}
      actions={
        canManage
          ? {
              act: rowAct,
              onChanged: () => void refresh(),
              onTriggers: () => openTriggers(agent),
              onEdit: () => openDialog(agent),
              onOpenProject,
              onPair:
                agent.kind === "foreign"
                  ? () => {
                      setPairFor(agent.id);
                      setPairOpen(true);
                    }
                  : undefined,
            }
          : undefined
      }
    />
  );

  /** The chat, scrolled to the question — what a tap on "needs you" means. */
  const openAt = (agentId: string, messageId: string | null) => {
    if (messageId) focusAgentMessage(agentId, messageId);
    onSelect(agentId);
  };

  const [focusMcp, setFocusMcp] = useState(false);
  const openDialog = (agent: AgentWithContext | null, section?: "mcp") => {
    setEditing(agent);
    setFocusMcp(section === "mcp");
    setDialogOpen(true);
  };
  // Triggers open for the agent they were asked from — a row's menu may name one whose
  // chat is not the open one. The id is kept after closing so the dialog can animate out.
  const [triggersFor, setTriggersFor] = useState<string | null>(null);
  const [triggersOpen, setTriggersOpen] = useState(false);
  const triggersAgent = agents.find((agent) => agent.id === triggersFor) ?? null;
  const openTriggers = (agent: AgentWithContext) => {
    setTriggersFor(agent.id);
    setTriggersOpen(true);
  };
  const handles = useMemo(() => agentHandles(users), [users]);
  useEffect(() => {
    if (newAgentRequest > 0) openDialog(null);
  }, [newAgentRequest]);
  /** Add foreign agent (the + menu), and a row menu's "Pair with a machine…". */
  const [foreignOpen, setForeignOpen] = useState(false);
  const [pairFor, setPairFor] = useState<string | null>(null);
  const [pairOpen, setPairOpen] = useState(false);
  const pairAgent = agents.find((agent) => agent.id === pairFor) ?? null;

  return (
    // A container, so list-and-chat versus one pane follows the room this view has —
    // the screen on a phone, its window on the desktop — not the viewport.
    <div className="@container/agents flex min-h-0 min-w-0 flex-1">
      <aside
        className={cn(
          "flex min-h-0 w-full shrink-0 flex-col bg-surface/40",
          pane === "chat" && "hidden",
          // A list window with a chat window beside it is the list alone, full width.
          !listBesideChat && "border-r border-border/70 @3xl/agents:w-80",
          pane === "both" && selected && "hidden @3xl/agents:flex",
        )}
      >
        {showTitle ? (
          <div className="flex items-center gap-2 px-4 pb-2 pt-3.5">
            <h1 className="min-w-0 flex-1 text-base font-semibold tracking-tight">Agents</h1>
            {canManage ? (
              <DropdownMenu>
                <Hint label="New agent">
                  <DropdownMenuTrigger asChild>
                    <Button size="icon-sm" variant="ghost" aria-label="New agent">
                      <Plus />
                    </Button>
                  </DropdownMenuTrigger>
                </Hint>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuItem onSelect={() => openDialog(null)}>
                    <Bot /> New agent
                    <span className="ml-auto text-[11px] text-muted-foreground">in a project here</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setForeignOpen(true)}>
                    <Laptop /> Add foreign agent
                    <span className="ml-auto text-[11px] text-muted-foreground">on your machine</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>
        ) : null}
        <div className={cn("px-3 pb-2", !showTitle && "pt-3")}>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search agents"
              className="h-8 pl-8 text-[13px]"
            />
          </div>
        </div>
        <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto px-2 pb-3 scrollbar-slim" {...mouseDrop}>
          {!loading && agents.length === 0 ? (
            <div className="px-3 py-8 text-center text-[13px] text-muted-foreground">
              <p>No agents yet.</p>
              {canManage ? (
                <Button size="sm" className="mt-3" onClick={() => openDialog(null)}>
                  <Plus /> Create one
                </Button>
              ) : (
                <p className="mt-1">An admin can create one.</p>
              )}
            </div>
          ) : null}
          {master ? row(master) : null}
          {pinned.length > 0 ? (
            <>
              <ListGroupLabel divider={master !== null}>Pinned</ListGroupLabel>
              {pinned.map(row)}
            </>
          ) : null}
          {folderGroups.length > 0 ? (
            <div className={cn((master || pinned.length > 0) && "mt-1.5 border-t border-border/60 pt-1.5")}>
              {folderGroups.map((group) => (
                <FolderSection
                  key={group.folder.id}
                  group={group}
                  dropTarget={overKey === `folder:${group.folder.id}`}
                  renaming={renaming === group.folder.id}
                  canArrange={canArrange}
                  onToggle={() => setFolderOpen(group.folder.id, !group.expanded)}
                  onStartRename={() => {
                    setFolderOpen(group.folder.id, true);
                    setRenaming(group.folder.id);
                  }}
                  onRename={(name) => {
                    setRenaming(null);
                    if (name && name !== group.folder.name) folderActions.rename(group.folder.id, name);
                  }}
                  onUngroup={() => setUngrouping(group.folder)}
                >
                  {group.members.map(row)}
                </FolderSection>
              ))}
            </div>
          ) : null}
          {/* The loose list is also where a member is dropped to leave its folder. */}
          <div data-drop="loose">
            {loose.length > 0 || dragging?.folderId ? (
              pinned.length > 0 || folderGroups.length > 0 ? (
                <ListGroupLabel divider>All agents</ListGroupLabel>
              ) : master ? (
                <div className="mx-2.5 my-1.5 border-t border-border/60" aria-hidden />
              ) : null
            ) : null}
            {dragging?.folderId ? (
              <div
                className={cn(
                  "mx-1 mb-1 rounded-lg border border-dashed px-3 py-2.5 text-center text-[12px] text-muted-foreground transition-colors",
                  overKey === "loose" ? "border-primary bg-primary/10 text-foreground" : "border-border",
                )}
              >
                Drop here to take {dragging.name} out of its folder
              </div>
            ) : null}
            {loose.map(row)}
          </div>
        </div>
      </aside>

      <section
        className={cn(
          "flex min-h-0 min-w-0 flex-1 flex-col",
          pane === "list" && listBesideChat ? "hidden" : pane !== "chat" && !selected && "hidden @3xl/agents:flex",
        )}
      >
        {selected && !canManage ? (
          // The chat is transcript — tool output included — and talking to an agent is
          // a shell on this machine, so both are admin's; the server refuses them anyway.
          <>
            <header className="flex items-center gap-3 border-b border-border/70 px-4 pb-2.5 pt-[max(0.625rem,env(safe-area-inset-top))] win-md:px-6">
              <Button variant="ghost" size="icon-sm" className="@3xl/agents:hidden" aria-label="Back to agents" onClick={() => onSelect(null)}>
                <ArrowLeft />
              </Button>
              <AgentAvatar agent={selected} size="lg" />
              <p className="min-w-0 flex-1 truncate text-sm font-semibold">
                {selected.name} <span className="font-normal text-muted-foreground">@{selected.handle}</span>
              </p>
            </header>
            <div className="grid flex-1 place-items-center p-6">
              <EmptyState icon={<Bot />} title="Admins only" hint="Only an admin or the owner can read an agent's chat or talk to it." />
            </div>
          </>
        ) : selected ? (
          <AgentChat
            key={selected.id}
            agent={selected}
            agents={agents}
            users={users}
            now={now}
            standalone={pane === "chat"}
            onBack={() => onSelect(null)}
            onEdit={() => openDialog(selected)}
            onManageMcp={() => openDialog(selected, "mcp")}
            onTriggers={() => openTriggers(selected)}
            onChanged={() => void refresh()}
            onOpenTask={onOpenTask}
            boards={boards}
            onOpenBoard={onOpenBoard}
            onOpenProject={onOpenProject}
          />
        ) : (
          <div className="grid flex-1 place-items-center p-6">
            <EmptyState
              icon={<Bot />}
              title={agents.length ? "Pick an agent" : "No agents yet"}
              hint={
                pane === "list"
                  ? "Its chat opens in a window beside this list. Each agent is one Claude Code session in a project."
                  : "Each agent is one Claude Code session in a project. Talk to it here, @mention it on a card, or assign it one."
              }
            />
          </div>
        )}
      </section>

      {triggersAgent ? (
        <AgentTriggersDialog agent={triggersAgent} open={triggersOpen} onOpenChange={setTriggersOpen} now={now} handles={handles} />
      ) : null}

      <UngroupDialog
        folder={ungrouping}
        members={ungrouping ? agents.filter((agent) => agent.folderId === ungrouping.id) : []}
        onClose={() => setUngrouping(null)}
        onConfirm={(folderId) => {
          setUngrouping(null);
          folderActions.ungroup(folderId);
        }}
      />

      <AgentDialog
        agent={editing}
        projects={projects}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSaved={(agent) => {
          void refresh();
          onSelect(agent.id);
        }}
        onDeleted={() => {
          onSelect(null);
          void refresh();
        }}
        onManageProjects={onManageProjects}
        focusMcp={focusMcp}
      />

      {canManage ? (
        <>
          <ForeignAgentDialog
            open={foreignOpen}
            onOpenChange={setForeignOpen}
            onCreated={(agent) => {
              void refresh();
              onSelect(agent.id);
            }}
          />
          {pairAgent ? <PairingDialog agent={pairAgent} open={pairOpen} onOpenChange={setPairOpen} /> : null}
        </>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ list */

function previewOf(agent: AgentWithContext): string {
  const last = agent.lastMessage;
  if (!last) {
    if (agent.kind === "foreign") return "Claude Code on its own machine, through board-link";
    return agent.project ? `Works in ${agent.project.name}` : "Runs the app for you: projects, clones, boards, agents";
  }
  const text = previewText(last.body);
  return last.role === "user" ? `You: ${text}` : text;
}

/** A group's heading in the list: small, and over a thin rule when a group sits above it. */
function ListGroupLabel({ divider, children }: { divider: boolean; children: ReactNode }) {
  return (
    <p
      className={cn(
        "mx-2.5 px-0 pb-1 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground",
        divider ? "mt-1.5 border-t border-border/60 pt-2" : "pt-1",
      )}
    >
      {children}
    </p>
  );
}

/** A still release after a lift: open that row's menu at the finger. `nonce` makes a second one a change. */
type MenuRequest = { agentId: string; x: number; y: number; nonce: number };

/** Where a menu opens inside `box`: at the point, clamped, since a keyboard's menu key reports none. */
const anchorIn = (box: DOMRect | undefined, point: { x: number; y: number }) =>
  box
    ? { x: Math.min(Math.max(point.x - box.left, 0), box.width), y: Math.min(Math.max(point.y - box.top, 0), box.height) }
    : { x: 0, y: 0 };

/**
 * A folder on the list: a header (name, how many, a chevron) over its members.
 * Collapsed, the header still says when a member is waiting on the user or
 * working, so a closed folder never hides a question; a tap opens it. Held or
 * right-clicked (admins), it offers Rename, Ungroup and Collapse/Expand. The
 * header and the open body are both drop targets: whatever lands joins it.
 */
function FolderSection({
  group,
  dropTarget,
  renaming,
  canArrange,
  onToggle,
  onStartRename,
  onRename,
  onUngroup,
  children,
}: {
  group: FolderGroup;
  dropTarget: boolean;
  renaming: boolean;
  canArrange: boolean;
  onToggle: () => void;
  onStartRename: () => void;
  /** The name typed, or null when the edit was cancelled. */
  onRename: (name: string | null) => void;
  onUngroup: () => void;
  children: ReactNode;
}) {
  const { folder, expanded, memberCount, pinnedCount, needsYou, working } = group;
  const wrapper = useRef<HTMLDivElement>(null);
  const [menu, setMenu] = useState(false);
  const [anchor, setAnchor] = useState({ x: 0, y: 0 });
  const hold = useLongPress({
    contextMenu: true,
    disabled: !canArrange || renaming,
    onLongPress: (point) => {
      setAnchor(anchorIn(wrapper.current?.getBoundingClientRect(), point));
      setMenu(true);
    },
  });
  return (
    <div data-drop={`folder:${folder.id}`} className="pb-0.5">
      <div ref={wrapper} className="relative select-none [-webkit-touch-callout:none]" {...(renaming ? {} : hold)}>
        {canArrange ? (
          <DropdownMenu open={menu} onOpenChange={setMenu}>
            <DropdownMenuTrigger asChild>
              <span aria-hidden className="pointer-events-none absolute size-0" style={{ left: anchor.x, top: anchor.y }} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-52">
              <DropdownMenuItem onSelect={onStartRename}>
                <Pencil /> Rename
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={onToggle}>
                {expanded ? <FolderClosed /> : <FolderOpen />} {expanded ? "Collapse" : "Expand"}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={onUngroup}>
                <Ungroup /> Ungroup
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
        {renaming ? (
          <div className="flex items-center gap-2 rounded-lg px-2.5 py-1.5">
            <FolderOpen className="size-4 shrink-0 text-muted-foreground" />
            <FolderNameInput initial={folder.name} onDone={onRename} />
          </div>
        ) : (
          <button
            onClick={onToggle}
            aria-expanded={expanded}
            className={cn(
              "flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left transition-colors",
              dropTarget ? "bg-primary/15 ring-2 ring-primary/60" : menu ? "bg-muted/80" : "hover:bg-muted/50",
            )}
          >
            <ChevronRight className={cn("size-3.5 shrink-0 text-muted-foreground transition-transform", expanded && "rotate-90")} />
            {expanded ? (
              <FolderOpen className="size-4 shrink-0 text-muted-foreground" />
            ) : (
              <Folder className="size-4 shrink-0 text-muted-foreground" />
            )}
            <span className="min-w-0 flex-1 truncate text-[13px] font-semibold">{folder.name}</span>
            {pinnedCount > 0 ? (
              <Pin aria-label={`${pinnedCount} pinned`} className="size-3 shrink-0 rotate-45" style={{ color: "var(--kind-review)" }} />
            ) : null}
            {!expanded && working > 0 ? (
              <span
                aria-label={`${working} working`}
                className="size-1.5 shrink-0 rounded-full"
                style={{ background: agentStatusColor("working") }}
              />
            ) : null}
            {!expanded && needsYou > 0 ? (
              <span
                aria-label={`${needsYou} waiting on you`}
                className="shrink-0 rounded-full bg-waiting/15 px-1.5 text-[10px] font-semibold tabular-nums text-waiting"
              >
                {needsYou} needs you
              </span>
            ) : null}
            <span className="shrink-0 rounded-full bg-muted px-1.5 text-[10.5px] tabular-nums text-muted-foreground">{memberCount}</span>
          </button>
        )}
      </div>
      {expanded ? <div className="ml-3 border-l border-border/60 pl-1">{children}</div> : null}
    </div>
  );
}

/** The folder's name, edited in place: Enter (or leaving the box) saves, Esc cancels. Opens selected. */
function FolderNameInput({ initial, onDone }: { initial: string; onDone: (name: string | null) => void }) {
  const [value, setValue] = useState(initial);
  const done = useRef(false);
  const finish = (name: string | null) => {
    if (done.current) return;
    done.current = true;
    onDone(name);
  };
  return (
    <Input
      autoFocus
      aria-label="Folder name"
      value={value}
      maxLength={40}
      onFocus={(event) => event.currentTarget.select()}
      onChange={(event) => setValue(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === "Enter") finish(value.trim() || null);
        else if (event.key === "Escape") {
          event.stopPropagation();
          finish(null);
        }
      }}
      onBlur={() => finish(value.trim() || null)}
      className="h-7 min-w-0 flex-1 px-2 text-[13px] font-semibold"
    />
  );
}

/** Ungroup asks first, naming whom it lets loose: nothing about the agents changes but where they sit. */
function UngroupDialog({
  folder,
  members,
  onClose,
  onConfirm,
}: {
  folder: AgentFolder | null;
  members: AgentWithContext[];
  onClose: () => void;
  onConfirm: (folderId: string) => void;
}) {
  // Kept while the dialog animates out.
  const last = useRef<{ folder: AgentFolder; members: AgentWithContext[] } | null>(null);
  if (folder) last.current = { folder, members };
  const shown = last.current;
  return (
    <Dialog open={folder !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Ungroup “{shown?.folder.name}”?</DialogTitle>
          <DialogDescription>
            The folder goes and its {shown?.members.length === 1 ? "agent goes" : `${shown?.members.length ?? 0} agents go`} back to
            the list. Nothing else about them changes.
          </DialogDescription>
        </DialogHeader>
        <ul className="space-y-1.5">
          {shown?.members.map((agent) => (
            <li key={agent.id} className="flex items-center gap-2 text-[13px]">
              <AgentAvatar agent={agent} size="sm" />
              <span className="truncate">{agent.name}</span>
              <span className="truncate text-muted-foreground">@{agent.handle}</span>
            </li>
          ))}
        </ul>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => shown && onConfirm(shown.folder.id)}>Ungroup</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AgentRow({
  agent,
  now,
  active,
  arrange,
  onSelect,
  onOpenBeside,
  onOpenQuestion,
  onTogglePin,
  actions,
}: {
  agent: AgentWithContext;
  now: number;
  active: boolean;
  /** Admins, any agent but the master: it drags, and a still release after a lift opens its menu. */
  arrange?: {
    lifted: boolean;
    dragging: boolean;
    dropTarget: boolean;
    menuRequest: MenuRequest | null;
    onDragStart: (event: React.DragEvent) => void;
    onDragEnd: () => void;
  };
  onSelect: () => void;
  /** "Open in new window", where the list is a window of its own. */
  onOpenBeside?: () => void;
  onOpenQuestion: (messageId: string | null) => void;
  /** Offered on hover (a desktop); a touch screen pins from the row's hold menu. Absent for the master. */
  onTogglePin?: () => void;
  /** The hold / right-click menu (admins): the chat's ⋯ items, for this agent. */
  actions?: {
    act: AgentAct;
    onChanged: () => void;
    onTriggers: () => void;
    onEdit: () => void;
    onOpenProject?: (projectId: string) => void;
    /** A foreign agent: a fresh pairing code for its machine. */
    onPair?: () => void;
  };
}) {
  const needsYou = agentNeedsYou(agent);
  const wrapper = useRef<HTMLDivElement>(null);
  const [menu, setMenu] = useState(false);
  // Where the menu opens, inside the row: under the finger or the pointer.
  const [anchor, setAnchor] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);
  const hold = useLongPress({
    contextMenu: true,
    disabled: !actions,
    // A row that drags is held by the touch-drag machine, which opens this menu itself.
    touch: !arrange,
    onLongPress: (point) => {
      setAnchor(anchorIn(wrapper.current?.getBoundingClientRect(), point));
      setMenu(true);
    },
  });
  const request = arrange?.menuRequest;
  useEffect(() => {
    if (!request || !actions) return;
    setAnchor(anchorIn(wrapper.current?.getBoundingClientRect(), request));
    setMenu(true);
    // Only a new request opens it; the same one again is the same release.
  }, [request?.nonce]);
  const stopControl = useStopAgent({
    agentId: agent.id,
    agentName: agent.name,
    working: agent.status === "working",
    stopping: agent.stopping,
    onStopped: actions?.onChanged,
  });
  const act: AgentAct = async (label, fn) => {
    if (!actions) return;
    setBusy(true);
    try {
      await actions.act(label, fn);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      ref={wrapper}
      className={cn(
        "group relative select-none rounded-lg transition-[transform,box-shadow,opacity] duration-150 [-webkit-touch-callout:none]",
        arrange?.lifted && "z-10 scale-[1.02] bg-surface shadow-lg",
        arrange?.dragging && "opacity-40",
        arrange?.dropTarget && "ring-2 ring-primary/60",
      )}
      data-agent-drag={arrange ? agent.id : undefined}
      data-drop={arrange ? `agent:${agent.id}` : undefined}
      draggable={FINE_POINTER && arrange !== undefined}
      onDragStart={arrange?.onDragStart}
      onDragEnd={arrange?.onDragEnd}
      {...hold}
    >
      {actions ? (
        <DropdownMenu open={menu} onOpenChange={setMenu}>
          <DropdownMenuTrigger asChild>
            <span aria-hidden className="pointer-events-none absolute size-0" style={{ left: anchor.x, top: anchor.y }} />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-60">
            <AgentActionItems
              agent={agent}
              queued={agent.pendingTurns}
              stopControl={stopControl}
              busy={busy}
              act={act}
              onOpen={onSelect}
              onOpenBeside={onOpenBeside}
              onTriggers={actions.onTriggers}
              onEdit={actions.onEdit}
              onOpenProject={actions.onOpenProject}
              onPair={actions.onPair}
            />
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
      <button
        onClick={onSelect}
        className={cn(
          "flex w-full items-center gap-3 rounded-lg px-2.5 py-2.5 text-left transition-colors",
          menu ? "bg-muted/80" : active ? "bg-accent/55" : "hover:bg-muted/50",
        )}
      >
        <AgentAvatar agent={agent} />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <p className="flex min-w-0 flex-1 items-center gap-1 text-[13.5px] font-medium">
              <span className="truncate">{agent.name}</span>
              {agent.pinned ? (
                <Pin aria-label="pinned" className="size-3 shrink-0 rotate-45" style={{ color: "var(--kind-review)" }} />
              ) : null}
              <FallbackPill agent={agent} now={now} compact />
              <DeployPausePill agent={agent} compact />
              <AgentPausePill agent={agent} now={now} />
              <McpCountChip agent={agent} />
              <PreviewsButton agentId={agent.id} compact />
            </p>
            <span
              className={cn(
                "shrink-0 text-[10.5px] tabular-nums text-muted-foreground",
                onTogglePin && "group-hover:invisible group-has-[[data-pin]:focus-visible]:invisible",
              )}
            >
              {relativeTime(agent.activityAt, now)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <p
              className={cn(
                "min-w-0 flex-1 truncate text-[12px]",
                needsYou ? "font-medium text-foreground" : "text-muted-foreground",
              )}
            >
              {agent.pausedForDeploy ? (
                <span style={{ color: agentStatusColor("working") }}>paused for a deploy, resuming after it…</span>
              ) : agent.status === "working" ? (
                <span style={{ color: agentStatusColor("working") }}>working…</span>
              ) : (
                previewOf(agent)
              )}
            </p>
            {needsYou ? (
              <NeedsYouPill questions={agent.openQuestions ?? []} now={now} onOpen={onOpenQuestion} />
            ) : agent.pendingTurns > 0 ? (
              <span className="shrink-0 rounded-full bg-primary/15 px-1.5 text-[10px] font-semibold tabular-nums text-primary">
                {agent.pendingTurns}
              </span>
            ) : null}
          </div>
          <ForeignRowLine agent={agent} now={now} />
        </div>
      </button>
      {onTogglePin ? (
        <Hint label={agent.pinned ? "Unpin" : "Pin to the top"}>
          <button
            data-pin
            aria-label={agent.pinned ? `Unpin ${agent.name}` : `Pin ${agent.name} to the top`}
            onClick={onTogglePin}
            className="absolute right-2 top-1.5 hidden size-6 place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:grid group-hover:grid"
          >
            {agent.pinned ? <PinOff className="size-3.5" /> : <Pin className="size-3.5" />}
          </button>
        </Hint>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ chat */

/**
 * Copying out of the chat: the selection's structure without the page's styles. A
 * plain copy took the dark theme's computed colours along, which pasted into Outlook
 * as text on a black box.
 */
function copyClean(event: React.ClipboardEvent<HTMLDivElement>) {
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) return;
  // A field being typed in copies as it always did.
  if ((event.target as HTMLElement).closest?.("input, textarea")) return;
  const parts: DocumentFragment[] = [];
  for (let index = 0; index < selection.rangeCount; index++) parts.push(selection.getRangeAt(index).cloneContents());
  const fragment = document.createDocumentFragment();
  parts.forEach((part) => fragment.appendChild(part));
  event.preventDefault();
  event.clipboardData.setData("text/html", `<html><body>${cleanSelectionHtml(fragment)}</body></html>`);
  event.clipboardData.setData("text/plain", selection.toString());
}

/** An open question the transcript has not loaded, as enough of a message for the composer's chip. */
function askStub(agentId: string, question: AgentOpenQuestion): AgentMessage {
  return {
    id: question.messageId,
    agentId,
    turnId: null,
    role: "agent",
    kind: "question",
    authorId: agentId,
    body: question.body,
    data: { status: "open", options: question.options },
    taskId: question.taskId,
    createdAt: question.createdAt,
    updatedAt: question.createdAt,
  };
}

/**
 * The transcript as it grows: an infinite query, one page of 200 going back per
 * `loadEarlier`. It is never refetched by the live feed — `agent.message` events
 * are written straight into the cached pages (`LiveSyncProvider`), which also
 * carries a tool call's result arriving on an existing row — so reopening a chat
 * renders what is cached at once. While the websocket is down the newest page is
 * re-read every few seconds and merged in, as it always was.
 */
function useTranscript(agentId: string, working: boolean) {
  const client = useQueryClient();
  const query = useInfiniteQuery({
    queryKey: qk.agentMessages(agentId),
    queryFn: async ({ pageParam }): Promise<TranscriptPage> => {
      const { messages } = await api.agentMessages(agentId, { before: pageParam ?? undefined, limit: TRANSCRIPT_PAGE });
      return { messages, more: messages.length >= TRANSCRIPT_PAGE };
    },
    initialPageParam: null as TranscriptCursor,
    getNextPageParam: olderCursor,
    // Kept current by events, not by staleness; and bounded, because a transcript is big.
    staleTime: Infinity,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const messages = useMemo(() => flattenTranscript(query.data), [query.data]);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  /** Nothing cached to show yet. */
  const loading = query.isPending;
  /** Text the agent is writing right now, per turn. Never stored. */
  const [draft, setDraft] = useState<string>("");
  /**
   * The turn the current draft belongs to, and how many of that turn's text
   * messages were already committed when it started. The draft is superseded once
   * another committed text message for that turn lands — whether that arrives as a
   * `agent.message` event or via the reconciliation poll below — so the streamed
   * tail never lingers as a duplicate of the stored reply.
   */
  const draftTurn = useRef<string | null>(null);
  const draftBaseline = useRef(0);
  const committedText = useCallback(
    (turnId: string | null) =>
      turnId === null
        ? 0
        : messagesRef.current.filter((m) => m.turnId === turnId && m.role === "agent" && m.kind === "text").length,
    [],
  );
  const connected = useLiveConnected();

  const upsert = useCallback(
    (incoming: AgentMessage[]) =>
      client.setQueryData<TranscriptData>(qk.agentMessages(agentId), (data) => upsertTranscript(data, incoming)),
    [client, agentId],
  );

  /**
   * Reconcile the newest page from the server on a timer. Transcript rows are
   * written outside `write()`, so the revision poll that heals every other query
   * never touches them, and a single dropped `agent.message`/`agent.delta` push
   * (Redis has no replay) would otherwise leave an open chat showing a stale
   * "latest" or stuck on "working…" until a manual refresh. Fast while the agent
   * is working so a lost reply surfaces within seconds; slow while idle; fastest
   * when the socket is down, which is the only source then.
   */
  useEffect(() => {
    const period = !connected ? 4000 : working ? 6000 : 25_000;
    const handle = setInterval(() => {
      void api
        .agentMessages(agentId, { limit: TRANSCRIPT_PAGE })
        .then(({ messages: latest }) => {
          upsert(latest);
          // A committed reply the poll just brought in supersedes the live draft.
          if (draftTurn.current && committedText(draftTurn.current) > draftBaseline.current) setDraft("");
        })
        .catch(() => undefined);
    }, period);
    return () => clearInterval(handle);
  }, [connected, working, agentId, upsert, committedText]);

  // The messages themselves land in the cache from the app-wide listener; only the typing is here.
  useLiveEvent((event) => {
    if (!("agentId" in event) || event.agentId !== agentId) return;
    if (event.type === "agent.message") {
      if (event.message.role === "agent" && event.message.kind === "text") setDraft("");
    } else if (event.type === "agent.delta") {
      setDraft((current) => {
        if (current === "") {
          // A fresh block: remember its turn and the committed count to beat.
          draftTurn.current = event.turnId;
          draftBaseline.current = committedText(event.turnId);
        }
        return current + event.text;
      });
    } else if (event.type === "agent.delta.reset") {
      setDraft("");
    }
  });

  // Read through refs, so a jump can call these in a loop without a stale page.
  const latest = useRef(messages);
  latest.current = messages;
  const hasEarlierRef = useRef(query.hasNextPage);
  hasEarlierRef.current = query.hasNextPage;
  const fetchNextPage = query.fetchNextPage;
  /** One page further back; false once there is nothing earlier. */
  const loadEarlier = useCallback(async (): Promise<boolean> => {
    if (!hasEarlierRef.current) return false;
    const result = await fetchNextPage();
    if (result.isError) throw result.error;
    // Into the refs at once, so the next page starts before these and `has` sees them.
    latest.current = flattenTranscript(result.data);
    hasEarlierRef.current = result.hasNextPage;
    return result.hasNextPage;
  }, [fetchNextPage]);
  const find = useCallback((messageId: string) => latest.current.find((message) => message.id === messageId) ?? null, []);
  const has = useCallback((messageId: string) => find(messageId) !== null, [find]);

  return { messages, loading, draft, hasEarlier: query.hasNextPage, loadEarlier, has, find, upsert };
}

/** The agent's board as a chip in the chat header, the rest behind "+N". */
function LinkedBoards({ boards, onOpen }: { boards: Board[]; onOpen: (boardId: string) => void }) {
  const [first, ...rest] = boards;
  if (!first) return null;
  return (
    <span className="flex min-w-0 shrink items-center gap-1">
      <button
        type="button"
        data-linked-board={first.id}
        title={`Open board ${first.name}`}
        className="flex min-w-0 max-w-[11rem] items-center gap-1 rounded-md border border-border/70 px-1.5 py-px text-[11px] font-medium text-foreground/90 hover:bg-accent hover:text-foreground"
        onClick={() => onOpen(first.id)}
      >
        <LayoutGrid className="size-3 shrink-0 text-muted-foreground" />
        <span className="truncate">{first.name}</span>
      </button>
      {rest.length > 0 ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={`${boards.length} boards in this project`}
              className="shrink-0 rounded-md border border-border/70 px-1 py-px text-[11px] font-medium hover:bg-accent hover:text-foreground"
            >
              +{rest.length}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            {boards.map((board) => (
              <DropdownMenuItem key={board.id} onSelect={() => onOpen(board.id)}>
                <LayoutGrid /> <span className="truncate">{board.name}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </span>
  );
}

function AgentChat({
  agent,
  agents,
  users,
  now,
  onBack,
  onEdit,
  onManageMcp,
  onTriggers,
  onChanged,
  onOpenTask,
  boards,
  onOpenBoard,
  onOpenProject,
  standalone = false,
}: {
  agent: AgentWithContext;
  agents: AgentWithContext[];
  users: User[];
  now: number;
  /** The chat alone in its window (the desktop), its list being a window of its own. */
  standalone?: boolean;
  onBack: () => void;
  onEdit: () => void;
  /** Opens the edit dialog at its MCP tools section. */
  onManageMcp: () => void;
  onTriggers: () => void;
  onChanged: () => void;
  onOpenTask: (taskId: string) => void;
  boards: BoardDetail[];
  onOpenBoard?: (boardId: string) => void;
  onOpenProject?: (projectId: string) => void;
}) {
  const { messages, loading, draft, hasEarlier, loadEarlier, has, find, upsert } = useTranscript(agent.id, agent.status === "working");
  const voice = useVoiceSupport();
  const [autoSend, setAutoSend] = useAutoSend();
  // Consecutive tool calls read as one collapsed line rather than one row each.
  const items = useMemo(() => groupToolRuns(messages), [messages]);
  const canTalk = useCan("admin");
  const handles = useMemo(() => agentHandles(users), [users]);
  const names = useMemo(() => new Map(users.map((user) => [user.id, user.displayName])), [users]);
  // `@claude` has no chat to forward to, and tagging the agent you are talking to is noise.
  const mentions = useMemo(
    () => mentionCandidates(agents, { excludeId: agent.id, projectId: agent.projectId }),
    [agents, agent.id, agent.projectId],
  );
  const client = useQueryClient();
  const [busy, setBusy] = useState(false);

  /**
   * What the composer answers. A newly asked question becomes the reply target on
   * its own — the one thing the agent is waiting for is what the next message is
   * most likely to be — and the chip in the composer clears it.
   */
  const [replyTo, setReplyTo] = useState<AgentMessage | null>(null);
  const lastAsk = useRef<string | null>(null);
  useEffect(() => {
    // From the agent's open questions, not the loaded page: the newest can sit above
    // hours of transcript, and a reply typed without the chip used to leave it open.
    const open = agent.openQuestions ?? [];
    const newest = open[open.length - 1];
    if (newest && newest.messageId !== lastAsk.current) {
      lastAsk.current = newest.messageId;
      setReplyTo(messages.find((message) => message.id === newest.messageId) ?? askStub(agent.id, newest));
    }
    // Closed elsewhere (another tab, a button, the agent): stop answering it.
    const loaded = replyTo ? messages.find((message) => message.id === replyTo.id) : undefined;
    const stillOpen = replyTo && (loaded ? loaded.data?.status === "open" : open.some((question) => question.messageId === replyTo.id));
    if (replyTo && !stillOpen && replyTo.kind === "question") setReplyTo(null);
  }, [messages, agent.openQuestions]); // eslint-disable-line react-hooks/exhaustive-deps

  const openQuestions = agent.openQuestions ?? [];
  const waiting = useWaiting();
  /** From the pinned bar, which knows only the id: the question may not be loaded. */
  const respondTo = async (messageId: string, action: "answer" | "dismiss", body?: string) => {
    try {
      const { ask: closed, reply } = await api.respondToAgent(agent.id, messageId, { action, body });
      upsert([closed, ...(reply ? [reply.message] : [])]);
      if (replyTo?.id === messageId) setReplyTo(null);
      onChanged();
      void waiting.refresh();
    } catch (cause) {
      toast.error(cause instanceof ApiError ? cause.message : "Could not send that");
    }
  };

  const respond = async (ask: AgentMessage, action: "answer" | "accept" | "dismiss", body?: string) => {
    try {
      const { ask: closed, reply } = await api.respondToAgent(agent.id, ask.id, { action, body });
      upsert([closed, ...(reply ? [reply.message] : [])]);
      if (replyTo?.id === ask.id) setReplyTo(null);
      onChanged();
      void waiting.refresh();
    } catch (cause) {
      window.alert(cause instanceof ApiError ? cause.message : "Could not send that");
    }
  };

  const scroller = useRef<HTMLDivElement>(null);
  const pinned = useRef(true);
  // Mirrors `pinned` into render, so a "Jump to latest" button can show when the
  // reader has scrolled up and the conversation has moved on below them.
  const [atBottom, setAtBottom] = useState(true);
  const onScroll = () => {
    const el = scroller.current;
    if (!el) return;
    const bottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    pinned.current = bottom;
    setAtBottom(bottom);
  };
  const jumpToLatest = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    pinned.current = true;
    setAtBottom(true);
  }, []);
  // Follow the conversation while the reader is at the bottom; leave them be once
  // they have scrolled up to read something.
  useEffect(() => {
    const el = scroller.current;
    if (el && pinned.current && !jumping.current) el.scrollTop = el.scrollHeight;
  }, [messages, draft]);
  // Images load after the scroll above; keep following as the content grows.
  const content = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scroller.current;
    if (!el || !content.current) return;
    // Judged against the height *before* the growth: by the time this runs the
    // new content is already there, so "near the bottom" would read as false.
    let previous = el.scrollHeight;
    const observer = new ResizeObserver(() => {
      if (!jumping.current && (pinned.current || previous - el.scrollTop - el.clientHeight < 80)) {
        el.scrollTop = el.scrollHeight;
        pinned.current = true;
        setAtBottom(true);
      }
      previous = el.scrollHeight;
    });
    observer.observe(content.current);
    return () => observer.disconnect();
  }, []);

  /**
   * Scroll to one message and flash it — a question asked hours and a compaction
   * ago included: earlier pages are loaded until it is there. While jumping the
   * chat stops following the bottom, or the pages arriving above would pull it away.
   */
  const [highlight, setHighlight] = useState<string | null>(null);
  const jumping = useRef(false);
  const jumpTo = useCallback(
    async (messageId: string) => {
      jumping.current = true;
      pinned.current = false;
      for (let page = 0; !has(messageId) && page < 40; page++) {
        if (!(await loadEarlier().catch(() => false))) break;
      }
      if (!has(messageId)) {
        jumping.current = false;
        toast.error("Could not find that question in the chat");
        return;
      }
      setHighlight(messageId);
      // After the render that holds the pages just loaded.
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          scroller.current?.querySelector(`[data-message-id="${messageId}"]`)?.scrollIntoView({ block: "center" });
          jumping.current = false;
        }),
      );
      setTimeout(() => setHighlight((current) => (current === messageId ? null : current)), 2600);
    },
    [has, loadEarlier],
  );

  // A jump asked for from elsewhere (the list's pill, the Awaiting page, a card), once loaded.
  const focusRequest = useAgentFocusRequest(agent.id);
  useEffect(() => {
    if (loading || !focusRequest) return;
    const messageId = takeAgentFocus(agent.id);
    if (messageId) void jumpTo(messageId);
  }, [loading, focusRequest, agent.id, jumpTo]);

  // What waits behind the run. Re-read when the agent's own counters say it moved,
  // which the agents list (kept live by events) is the first to know.
  const turnsKey = qk.agentTurns(agent.id, "pending");
  const queuedQuery = useQuery({
    queryKey: turnsKey,
    queryFn: () => api.agentTurns(agent.id, ["pending"]),
    select: selectTurns,
    placeholderData: keepPrevious,
  });
  const queued = queuedQuery.data ?? NO_TURNS;
  const turnsSeen = useRef(`${agent.pendingTurns}|${agent.status}`);
  useEffect(() => {
    const seen = `${agent.pendingTurns}|${agent.status}`;
    if (seen === turnsSeen.current) return;
    turnsSeen.current = seen;
    void client.invalidateQueries({ queryKey: turnsKey, exact: true }, { cancelRefetch: false });
  }, [agent.pendingTurns, agent.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
      onChanged();
    } catch (cause) {
      window.alert(cause instanceof ApiError ? cause.message : `Could not ${label}`);
    } finally {
      setBusy(false);
    }
  };

  const linkedBoards = useMemo(
    () => boardsOfProject(boards, agent.projectId, agent.project?.name),
    [boards, agent.projectId, agent.project?.name],
  );

  const working = agent.status === "working";
  // The tool calls in flight right now, so the live tail can show what it is doing
  // (a running subagent, a build) rather than a bare "working…" that reads as frozen.
  const running = useMemo(
    () => (working ? messages.filter((message) => message.kind === "tool" && toolCallOf(message).status === "running") : []),
    [working, messages],
  );
  const stopControl = useStopAgent({ agentId: agent.id, agentName: agent.name, working, stopping: agent.stopping, onStopped: onChanged });
  const foreign = agent.kind === "foreign";
  const [pairing, setPairing] = useState(false);

  return (
    <>
      {/* On a phone this header stands in for the app's top bar, so it clears the notch. */}
      <header className="flex items-center gap-3 border-b border-border/70 px-4 pb-2.5 pt-[max(0.625rem,env(safe-area-inset-top))] win-md:px-6">
        {standalone ? (
          // A window of its own: the list is another window, brought up beside this one.
          <Hint label="Agents list">
            <Button variant="ghost" size="icon-sm" aria-label="Show the agents list" onClick={onBack}>
              <PanelLeftOpen />
            </Button>
          </Hint>
        ) : (
          <Button variant="ghost" size="icon-sm" className="@3xl/agents:hidden" aria-label="Back to agents" onClick={onBack}>
            <ArrowLeft />
          </Button>
        )}
        <AgentAvatar agent={agent} size="lg" />
        <div className="min-w-0 flex-1">
          <p className="flex min-w-0 items-center gap-1.5 text-sm font-semibold">
            <span className="truncate">
              {agent.name} <span className="font-normal text-muted-foreground">@{agent.handle}</span>
            </span>
            <FallbackPill agent={agent} now={now} />
            <DeployPausePill agent={agent} />
          </p>
          <p className="flex min-w-0 items-center gap-1.5 text-[11.5px] text-muted-foreground">
            {openQuestions.length > 0 ? (
              // What it is waiting on is one tap away, not somewhere up the chat.
              <button
                className="shrink-0 font-medium text-waiting underline-offset-2 hover:underline"
                onClick={() => void jumpTo(openQuestions[0]!.messageId)}
              >
                {agent.status === "waiting" ? AGENT_STATUS_LABELS.waiting : `${AGENT_STATUS_LABELS[agent.status]} · needs you`}
              </button>
            ) : (
              <span className="shrink-0" style={{ color: agentStatusColor(agent.status) }}>
                {AGENT_STATUS_LABELS[agent.status]}
              </span>
            )}
            <span className="shrink-0">·</span>
            {foreign ? (
              // On its own machine: which one, whether it is there, and the directory it runs in.
              <>
                <span className="flex shrink-0 items-center gap-1.5" title={agent.link?.platform ?? undefined}>
                  <PresenceDot agent={agent} />
                  {agent.link?.paired ? `${machineName(agent.link)} · ${presenceLabel(agent.link, now)}` : "not paired yet"}
                </span>
                {agent.link?.dir || agent.directory.path ? (
                  <span className="hidden min-w-0 items-center gap-1.5 win-sm:flex">
                    <Laptop className="size-3 shrink-0" />
                    <span className="truncate font-mono text-[11px]">{shortPath(agent.link?.dir || agent.directory.path)}</span>
                  </span>
                ) : null}
              </>
            ) : null}
            {!foreign && linkedBoards.length > 0 && onOpenBoard ? (
              <LinkedBoards boards={linkedBoards} onOpen={onOpenBoard} />
            ) : null}
            {/* Linked, the board says where the agent works and the path is a wide screen's
                detail — and it is what gives way, never the board's name. */}
            {foreign ? null : (
              <span
                className={cn(
                  "flex min-w-0 items-center gap-1.5",
                  linkedBoards.length > 0 && onOpenBoard && "hidden shrink-[100] win-lg:flex",
                )}
              >
                <FolderGit2 className="size-3 shrink-0" />
                <span className="truncate font-mono text-[11px]">
                  {agent.project ? shortPath(agent.project.path) : `${shortPath(agent.directory.path)} · workspace`}
                </span>
              </span>
            )}
          </p>
        </div>
        {/* Previews and attached MCP servers are this host's; a foreign agent has neither. */}
        {foreign ? null : <PreviewsButton agentId={agent.id} />}
        <ContextMeter agent={agent} />
        {foreign ? null : <McpToolsButton agent={agent} canManage={canTalk} onManage={onManageMcp} />}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon-sm" aria-label="Agent actions">
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            <AgentActionItems
              agent={agent}
              queued={queued.length}
              stopControl={stopControl}
              busy={busy}
              act={act}
              onTriggers={onTriggers}
              onEdit={onEdit}
              onOpenProject={onOpenProject}
              onPair={foreign ? () => setPairing(true) : undefined}
              chatItems={
                canTalk && voice.canListen ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={() => setAutoSend(!autoSend)}>
                      <Check className={cn(!autoSend && "opacity-0")} /> Auto-send dictation
                      <span className="ml-auto text-[11px] text-muted-foreground">when you stop</span>
                    </DropdownMenuItem>
                  </>
                ) : null
              }
            />
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      <PendingQuestionsBar
        questions={openQuestions}
        now={now}
        canRespond={canTalk}
        onJump={(messageId) => void jumpTo(messageId)}
        onAnswer={(messageId, option) => respondTo(messageId, "answer", option)}
        onReply={(messageId) => {
          const ask = find(messageId);
          if (ask) setReplyTo(ask);
          else void jumpTo(messageId).then(() => setReplyTo(find(messageId)));
        }}
        onDismiss={(messageId) => respondTo(messageId, "dismiss")}
        onOpenTask={onOpenTask}
      />

      <div className="relative flex min-h-0 flex-1 flex-col">
      <div
        ref={scroller}
        onScroll={onScroll}
        onCopy={copyClean}
        className="grain min-h-0 flex-1 overflow-y-auto scrollbar-slim [overflow-anchor:none]"
      >
        <div ref={content} className="mx-auto flex max-w-3xl flex-col gap-2.5 px-4 py-4 win-md:px-6">
          {hasEarlier ? (
            <button className="mx-auto text-[12px] text-muted-foreground hover:text-foreground" onClick={() => void loadEarlier()}>
              Load earlier messages
            </button>
          ) : null}
          {!loading && messages.length === 0 ? (
            <p className="py-10 text-center text-[13px] text-muted-foreground">
              {foreign
                ? `Nothing said yet. ${agent.name} runs on ${agent.link?.paired ? machineName(agent.link) : "a machine of yours once paired"}, with its own Claude login.`
                : agent.project
                ? `Nothing said yet. Ask ${agent.name} something about ${agent.project.name}.`
                : `Nothing said yet. Ask ${agent.name} to clone a repo, register a project, make a board or set up an agent for you.`}
            </p>
          ) : null}
          {items.map((item) => {
            if (item.type === "tools") return <ToolGroup key={item.key} messages={item.messages} />;
            const message = item.message;
            const email = message.role === "agent" ? emailOf(message) : null;
            if (email) {
              return (
                <EmailDraftCard
                  key={message.id}
                  agentId={agent.id}
                  agentName={agent.name}
                  message={message}
                  email={email}
                  now={now}
                  canEdit={canTalk}
                  highlighted={highlight === message.id}
                  onSaved={(saved) => upsert([saved])}
                />
              );
            }
            return message.kind === "question" || message.kind === "suggestion" ? (
              <AskCard
                key={message.id}
                highlighted={highlight === message.id}
                agentName={agent.name}
                message={message}
                handles={handles}
                now={now}
                canRespond={canTalk}
                replying={replyTo?.id === message.id}
                onReply={() => setReplyTo(message)}
                onRespond={(action, body) => void respond(message, action, body)}
                onOpenTask={onOpenTask}
              />
            ) : (
              <MessageRow
                key={message.id}
                message={message}
                names={names}
                handles={handles}
                now={now}
                onOpenTask={onOpenTask}
              />
            );
          })}
          {working ? <LiveTail draft={draft} running={running} handles={handles} now={now} /> : null}
        </div>
      </div>
        {!atBottom ? (
          <button
            type="button"
            aria-label="Jump to latest"
            onClick={jumpToLatest}
            className="absolute bottom-3 right-3 flex items-center gap-1 rounded-full border border-border/70 bg-background/90 px-2.5 py-1 text-[11.5px] font-medium text-foreground/90 shadow-sm backdrop-blur transition hover:bg-accent hover:text-foreground"
          >
            <ChevronDown className="size-3.5" /> Latest
            {working ? (
              <span className="ml-0.5 size-1.5 rounded-full" style={{ background: agentStatusColor("working") }} />
            ) : null}
          </button>
        ) : null}
      </div>

      {queued.length > 0 ? (
        <div className="mx-auto w-full max-w-3xl px-4 win-md:px-6">
          <div className="flex flex-wrap items-center gap-1.5 pb-1 text-[11.5px] text-muted-foreground">
            <CircleDot className="size-3" />
            {queued.length} queued{agent.status === "stopped" ? " until resumed" : working ? " behind this run" : ""}:
            {queued.slice(0, 3).map((turn) => (
              <span key={turn.id} className="inline-flex max-w-56 items-center gap-1 rounded-full bg-muted px-2 py-0.5">
                <span className="truncate">{previewText(turn.summary)}</span>
                {canTalk ? (
                  <button
                    aria-label="Cancel queued message"
                    className="hover:text-foreground"
                    onClick={() => void act("cancel", () => api.cancelAgentTurn(turn.id))}
                  >
                    <X className="size-3" />
                  </button>
                ) : null}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      <AgentPausedBanner
        agent={agent}
        queued={queued.length}
        now={now}
        busy={busy}
        onResume={canTalk ? () => void act("resume", () => api.resumeAgent(agent.id)) : undefined}
      />

      <ForeignWaitingBanner agent={agent} />
      {foreign && canTalk ? <PairingDialog agent={agent} open={pairing} onOpenChange={setPairing} /> : null}

      {canTalk ? (
        <AgentComposer
          agent={agent}
          mentions={mentions}
          queued={queued.length}
          stopControl={stopControl}
          replyTo={replyTo}
          onClearReply={() => setReplyTo(null)}
          onSent={(message) => {
            upsert([message]);
            // A typed reply may have answered a question: the bar, the pill and Awaiting re-read now.
            onChanged();
            void waiting.refresh();
          }}
        />
      ) : (
        <p className="border-t border-border/70 px-5 py-3 text-center text-[12px] text-muted-foreground">
          Only admins can talk to agents.
        </p>
      )}
    </>
  );
}

/**
 * While a run is live: the text it is writing, plus what it is doing right now —
 * the tool calls in flight, with any subagents named and timed. A bare "working…"
 * reads as frozen on a long run (a subagent can take many minutes); showing the
 * in-flight calls is the sign of life, and the one place a subagent's work shows.
 */
function LiveTail({ draft, running, handles, now }: { draft: string; running: AgentMessage[]; handles: Set<string>; now: number }) {
  const calls = running.map((message) => ({ message, call: toolCallOf(message) }));
  const subagents = calls.filter(({ call }) => call.name === "Task");
  const others = calls.filter(({ call }) => call.name !== "Task");
  const hasDraft = draft.trim().length > 0;
  const hasActivity = calls.length > 0;
  return (
    <div className="flex max-w-[92%] flex-col gap-2">
      {hasDraft ? (
        <div className="text-[13.5px] leading-relaxed">
          <Markdown text={draft} handles={handles} />
          <span className="ml-0.5 inline-block h-3.5 w-1.5 animate-pulse bg-foreground/60 align-middle" />
        </div>
      ) : null}
      {hasActivity ? (
        <div className="flex flex-col gap-1 rounded-lg border border-border/60 bg-muted/30 px-2.5 py-1.5">
          {subagents.map(({ message, call }) => (
            <div key={message.id} className="flex items-center gap-2 text-[12px]">
              <Bot className="size-3.5 shrink-0 animate-pulse" style={{ color: "var(--kind-active)" }} />
              <span className="min-w-0 flex-1 truncate">
                <span className="text-muted-foreground">Subagent · </span>
                <span className="text-foreground/90">{subagentLabel(call)}</span>
              </span>
              <span className="shrink-0 tabular-nums text-[10.5px] text-muted-foreground">{relativeTime(message.createdAt, now)}</span>
            </div>
          ))}
          {others.length > 0 ? (
            <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
              <Loader2 className="size-3.5 shrink-0 animate-spin" />
              <span className="min-w-0 flex-1 truncate">{summarizeTools(others.map(({ call }) => call))}…</span>
            </div>
          ) : null}
        </div>
      ) : hasDraft ? null : (
        <p className="flex items-center gap-2 text-[12.5px] text-muted-foreground">
          <Loader2 className="size-3.5 animate-spin" /> working…
        </p>
      )}
    </div>
  );
}

function MessageRow({
  message,
  names,
  handles,
  now,
  onOpenTask,
}: {
  message: AgentMessage;
  names: Map<string, string>;
  handles: Set<string>;
  now: number;
  onOpenTask: (taskId: string) => void;
}) {
  const stamp = <span className="text-[10.5px] tabular-nums text-muted-foreground">{relativeTime(message.createdAt, now)}</span>;
  const taskLink = message.taskId ? (
    <button className="text-[11.5px] text-primary underline-offset-2 hover:underline" onClick={() => onOpenTask(message.taskId!)}>
      open card
    </button>
  ) : null;

  if (message.role === "user") {
    const files = message.attachments ?? [];
    const onlyFiles = files.length > 0 && /^\(sent .*\)$/.test(message.body);
    return (
      <div className="flex flex-col items-end gap-1">
        {files.length > 0 ? <Attachments files={files} /> : null}
        {onlyFiles ? null : (
          <div className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-br-md bg-primary/15 px-3.5 py-2 text-[13.5px] leading-relaxed">
            <MentionText text={message.body} handles={handles} />
          </div>
        )}
        <div className="flex items-center gap-2 pr-1">
          <LiveState state={message.data?.live} />
          {message.authorId ? <span className="text-[10.5px] text-muted-foreground">{names.get(message.authorId) ?? message.authorId}</span> : null}
          {stamp}
        </div>
      </div>
    );
  }

  if (message.kind === "tool") return <ToolGroup messages={[message]} />;

  if (message.role === "system" && message.data?.trigger) {
    return <FiringRow message={message} />;
  }

  if (message.role === "system" && message.data?.compaction) {
    return <CompactionRow message={message} handles={handles} />;
  }

  if (message.role === "system") {
    const error = message.kind === "error";
    return (
      <div
        className={cn(
          "mx-auto flex max-w-[90%] items-start gap-2 rounded-md px-3 py-1.5 text-[12px]",
          error ? "bg-destructive/10 text-destructive" : "text-muted-foreground",
        )}
      >
        {error ? <AlertTriangle className="mt-0.5 size-3.5 shrink-0" /> : null}
        <span className="min-w-0 whitespace-pre-wrap [overflow-wrap:anywhere]">{message.body}</span>
        {taskLink}
      </div>
    );
  }

  const files = message.attachments ?? [];
  return (
    <div className="group flex max-w-[92%] flex-col gap-0.5">
      {message.body ? (
        <div className="text-[13.5px] leading-relaxed">
          <Markdown text={message.body} handles={handles} />
        </div>
      ) : null}
      {files.length > 0 ? <Attachments files={files} align="start" /> : null}
      <div className="flex items-center gap-2 opacity-0 transition-opacity group-hover:opacity-100">
        {stamp}
        {taskLink}
      </div>
    </div>
  );
}

/**
 * Where a message said to a busy agent went: into the run under way (read at its
 * next step, not after the turn), or — when the run ended before reading it — onto
 * the queue as a turn of its own. Nothing for a message that simply queued.
 */
function LiveState({ state }: { state: unknown }) {
  if (state === "pending" || state === "sent") {
    return (
      <span className="flex items-center gap-1 text-[10.5px] text-muted-foreground">
        <Loader2 className="size-3 animate-spin" /> sending to the run in progress
      </span>
    );
  }
  if (state === "delivered") {
    return (
      <span className="flex items-center gap-1 text-[10.5px]" style={{ color: "var(--kind-done)" }}>
        <Check className="size-3" /> read by the run in progress
      </span>
    );
  }
  if (state === "queued") {
    return (
      <span className="flex items-center gap-1 text-[10.5px] text-muted-foreground">
        <CircleDot className="size-3" /> queued — the run ended before reading it
      </span>
    );
  }
  return null;
}

/**
 * The agent asking something of the user. Visibly not one more paragraph: it is
 * the one message in the chat that needs them to act, so it gets a frame, the
 * answers it offered as buttons, and — once closed — says how it was closed.
 */
function AskCard({
  highlighted,
  agentName,
  message,
  handles,
  now,
  canRespond,
  replying,
  onReply,
  onRespond,
  onOpenTask,
}: {
  /** Just jumped to: flashed so the eye finds it. */
  highlighted: boolean;
  agentName: string;
  message: AgentMessage;
  handles: Set<string>;
  now: number;
  canRespond: boolean;
  replying: boolean;
  onReply: () => void;
  onRespond: (action: "answer" | "accept" | "dismiss", body?: string) => void;
  onOpenTask: (taskId: string) => void;
}) {
  const question = message.kind === "question";
  const data = (message.data ?? {}) as { status?: string; options?: string[]; reason?: string | null; via?: string; answer?: string };
  const open = data.status === "open";
  // A question is the one colour everything waiting on the user shares.
  const tint = question ? "var(--waiting)" : "var(--kind-active)";
  const Icon = question ? HelpCircle : Lightbulb;
  return (
    <div
      data-message-id={message.id}
      className={cn(
        "max-w-[92%] scroll-mt-4 rounded-xl border p-3 transition-shadow duration-500",
        !open && "opacity-75",
        highlighted && "ring-2 ring-waiting ring-offset-2 ring-offset-background",
      )}
      style={{
        borderColor: `color-mix(in oklab, ${tint} ${open ? 55 : 25}%, transparent)`,
        backgroundColor: `color-mix(in oklab, ${tint} ${open ? 9 : 4}%, transparent)`,
      }}
    >
      <p className="mb-1.5 flex items-center gap-1.5 text-[11.5px] font-semibold" style={{ color: tint }}>
        <Icon className="size-3.5" />
        {agentName} {question ? "is asking you" : "suggests"}
        <span className="font-normal text-muted-foreground">· {relativeTime(message.createdAt, now)}</span>
        {open && question ? <QuestionAge since={message.createdAt} now={now} markOnly /> : null}
        {!open ? (
          <span className="ml-auto rounded-full bg-muted px-1.5 text-[10px] font-medium text-muted-foreground">
            {data.status === "dismissed" && question ? "no longer needed" : data.status}
          </span>
        ) : null}
      </p>
      <div className="text-[13.5px] leading-relaxed">
        <Markdown text={message.body} handles={handles} />
      </div>
      {data.via === "agent" ? (
        <p className="mt-1.5 text-[12px] text-muted-foreground">
          {agentName} closed it {data.status === "moot" ? "as no longer needed" : "as answered"}
          {data.reason ? `: ${data.reason}` : "."}
        </p>
      ) : data.via === "chat" && typeof data.answer === "string" ? (
        <p className="mt-1.5 line-clamp-2 text-[12px] text-muted-foreground">Answered in the chat: {data.answer}</p>
      ) : null}
      {open && canRespond ? (
        <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
          {question
            ? (data.options ?? []).map((option) => (
                <Button key={option} size="xs" variant="outline" onClick={() => onRespond("answer", option)}>
                  {option}
                </Button>
              ))
            : null}
          {question ? (
            <Button size="xs" variant={replying ? "secondary" : "outline"} onClick={onReply}>
              <CornerDownRight /> {replying ? "Answering below" : "Answer"}
            </Button>
          ) : (
            <>
              <Button size="xs" onClick={() => onRespond("accept")}>
                <Check /> Accept
              </Button>
              <Button size="xs" variant="outline" onClick={onReply}>
                Accept with a note
              </Button>
            </>
          )}
          <Button size="xs" variant="ghost" onClick={() => onRespond("dismiss")}>
            {question ? (
              <>
                <X /> No longer needed
              </>
            ) : (
              "Dismiss"
            )}
          </Button>
        </div>
      ) : null}
      {message.taskId ? (
        <button className="mt-2 text-[11.5px] text-primary underline-offset-2 hover:underline" onClick={() => onOpenTask(message.taskId!)}>
          open the card
        </button>
      ) : null}
    </div>
  );
}

/**
 * How full the session's context is against the limit it will be compacted at,
 * and the button that compacts it now. The number is the input the model saw on
 * its last call, which is what the next turn pays for again.
 */
/** How full the session's context is. A readout; compacting lives in the ⋯ menu. */
function ContextMeter({ agent }: { agent: AgentWithContext }) {
  const limit = agent.effectiveContextLimit;
  const used = agent.sessionId ? agent.contextTokens : 0;
  const share = Math.min(used / limit, 1);
  const tint = share > 0.85 ? "var(--kind-blocked)" : share > 0.6 ? "var(--kind-review)" : "var(--kind-done)";
  const label = `${Math.round(used / 1000)}k / ${Math.round(limit / 1000)}k`;
  return (
    <Hint
      label={
        agent.sessionId
          ? `Context: ${used.toLocaleString()} of ${limit.toLocaleString()} tokens. Past the limit the conversation is summarised${
              agent.compactions ? ` (done ${agent.compactions}× so far)` : ""
            }. Compact now from the ⋯ menu.`
          : agent.memory
            ? "Fresh session: the next turn starts from the last compaction's summary."
            : "No session yet."
      }
    >
      <span
        tabIndex={0}
        aria-label={`Context ${label}`}
        className="hidden items-center gap-1.5 rounded-md px-1.5 py-1 text-[10.5px] tabular-nums text-muted-foreground win-sm:flex"
      >
        <span className="h-1.5 w-12 overflow-hidden rounded-full bg-muted">
          <span className="block h-full rounded-full" style={{ width: `${Math.max(share * 100, used ? 4 : 0)}%`, backgroundColor: tint }} />
        </span>
        {label}
      </span>
    </Hint>
  );
}

/** A trigger firing, with what came with it a click away. */
function FiringRow({ message }: { message: AgentMessage }) {
  const [open, setOpen] = useState(false);
  const payload = typeof message.data?.payload === "string" ? message.data.payload : "";
  return (
    <div className="mx-auto flex w-full max-w-[90%] flex-col items-center gap-1.5">
      <button
        className="flex items-center gap-1.5 rounded-full bg-muted/60 px-3 py-1 text-[11.5px] text-muted-foreground hover:text-foreground"
        onClick={() => payload && setOpen((value) => !value)}
      >
        <Zap className="size-3.5" style={{ color: "var(--kind-review)" }} />
        {message.body.replace(/^⚡\s*/, "")}
        {payload ? <ChevronRight className={cn("size-3 transition-transform", open && "rotate-90")} /> : null}
      </button>
      {open ? (
        <pre className="max-h-72 w-full overflow-auto whitespace-pre-wrap break-all rounded-md border border-border/60 bg-muted/40 p-2 font-mono text-[11px] scrollbar-slim">
          {payload}
        </pre>
      ) : null}
    </div>
  );
}

/** A compaction, with the summary the agent now works from one click away. */
function CompactionRow({ message, handles }: { message: AgentMessage; handles: Set<string> }) {
  const [open, setOpen] = useState(false);
  const summary = String(message.data?.summary ?? "");
  return (
    <div className="my-1 flex flex-col items-center gap-1.5">
      <div className="flex w-full items-center gap-3 text-[11.5px] text-muted-foreground">
        <span className="h-px flex-1 bg-border" />
        <button className="flex items-center gap-1.5 hover:text-foreground" onClick={() => setOpen((value) => !value)}>
          <Layers className="size-3.5" />
          Context compacted
          {typeof message.data?.fromTokens === "number" ? ` · ${Math.round(Number(message.data.fromTokens) / 1000)}k tokens summarised` : ""}
          <ChevronRight className={cn("size-3 transition-transform", open && "rotate-90")} />
        </button>
        <span className="h-px flex-1 bg-border" />
      </div>
      {open && summary ? (
        <div className="w-full rounded-lg border border-border/70 bg-muted/30 p-3 text-[12.5px] leading-relaxed">
          <Markdown text={summary} handles={handles} />
        </div>
      ) : null}
    </div>
  );
}

/**
 * Make public / Make private / Delete for a chat attachment: admin work on the
 * server, the cached transcript patched from the answer so the chip changes at
 * once. Making a file public copies its link, since that is what it was made
 * public for; making it private replaces the link, so the old one is dead.
 */
function useAttachmentActions() {
  const client = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const run = useCallback(
    async (file: AgentAttachment, action: () => Promise<{ attachment: AgentAttachment }>, done: (attachment: AgentAttachment) => void) => {
      setBusy(file.id);
      try {
        const { attachment } = await action();
        client.setQueriesData<TranscriptData>({ queryKey: ["agent-messages"] }, (data) => patchTranscriptAttachment(data, attachment));
        done(attachment);
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Could not change the file");
      } finally {
        setBusy(null);
      }
    },
    [client],
  );
  const setPublic = useCallback(
    (file: AgentAttachment, makePublic: boolean) =>
      run(file, () => api.setAgentAttachmentPublic(file.id, makePublic), (attachment) => {
        if (makePublic && attachment.url) copyFileLink(attachment.url, { success: () => toast.success("Public link copied"), error: toast.error });
        else toast.success(`${file.filename} is private again; it has a new link, and the old one no longer works`);
      }),
    [run],
  );
  const remove = useCallback(
    (file: AgentAttachment) => {
      if (!window.confirm(`Delete ${file.filename}? It is removed from the server and its link stops working.`)) return;
      void run(file, () => api.deleteAgentAttachment(file.id), () => toast.success(`${file.filename} deleted`));
    },
    [run],
  );
  return { busy, setPublic, remove };
}

const chipButton =
  "grid size-7 shrink-0 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50";

/** What a chip says about who can open a file's link. */
function linkNote(file: AgentAttachment): string {
  if (file.deleteAfterDownload) return " · deleted after download";
  if (file.shared) return " · public link";
  return "";
}

/**
 * Images as thumbnails that open in the viewer; text and PDFs as a chip opening the
 * stored file; any other file as a chip that downloads it. A file with a link
 * (everything an agent posts) has Copy link beside it — in the viewer's toolbar for
 * a picture. The link is private: it works for a signed-in admin, and for anyone
 * else only once an admin makes it public. Admins can make a file public or
 * private again, and delete it; a deleted file stays as a struck-through chip.
 */
function Attachments({ files, align = "end" }: { files: NonNullable<AgentMessage["attachments"]>; align?: "start" | "end" }) {
  const isAdmin = useCan("admin");
  const actions = useAttachmentActions();
  const images = files.filter((file) => file.kind === "image" && !file.deletedAt);
  const shareButton = (file: AgentAttachment, className: string) =>
    isAdmin && !file.deleteAfterDownload ? (
      <button
        type="button"
        className={className}
        disabled={actions.busy === file.id}
        onClick={() => void actions.setPublic(file, !file.shared)}
        aria-label={file.shared ? `Make ${file.filename} private` : `Make ${file.filename} public`}
        title={file.shared ? "Make private: only signed-in admins can open it, under a new link" : "Make public: anyone with the link can download it"}
      >
        {file.shared ? <Lock className="size-4" /> : <Globe className="size-4" />}
      </button>
    ) : null;
  const deleteButton = (file: AgentAttachment, className: string) =>
    isAdmin ? (
      <button
        type="button"
        className={className}
        disabled={actions.busy === file.id}
        onClick={() => actions.remove(file)}
        aria-label={`Delete ${file.filename}`}
        title="Delete the file"
      >
        <Trash2 className="size-4" />
      </button>
    ) : null;
  const gallery = useImageViewer(
    images.map((file) => ({
      src: api.agentAttachmentUrl(file.id),
      filename: file.filename,
      link: file.url,
      actions: (
        <>
          {shareButton(file, VIEWER_ICON_BUTTON)}
          {deleteButton(file, VIEWER_ICON_BUTTON)}
        </>
      ),
    })),
  );
  return (
    <div className={cn("flex max-w-[85%] flex-wrap items-start gap-1.5", align === "end" ? "justify-end" : "justify-start")}>
      {gallery.viewer}
      {files.map((file) =>
        file.deletedAt ? (
          <div
            key={file.id}
            className="flex max-w-72 items-center gap-2 rounded-xl border border-dashed border-border/80 px-2.5 py-1.5 text-[12px] text-muted-foreground"
            title={`Deleted ${new Date(file.deletedAt).toLocaleString()}`}
          >
            <Trash2 className="size-4 shrink-0" />
            <span className="min-w-0">
              <span className="block truncate line-through">{file.filename}</span>
              <span className="block text-[10.5px]">deleted · {humanBytes(file.bytes)}</span>
            </span>
          </div>
        ) : file.kind === "image" ? (
          <button
            key={file.id}
            type="button"
            onClick={() => gallery.open(images.indexOf(file))}
            title={file.filename}
            aria-label={`View ${file.filename}`}
            className="block cursor-zoom-in rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <img
              src={api.agentAttachmentUrl(file.id)}
              alt={file.filename}
              loading="lazy"
              className={cn(
                "rounded-xl border border-border/70 object-cover",
                // An agent's screenshot is the message; give it room to be read.
                align === "start" ? "max-h-80 max-w-full win-sm:max-w-md" : "max-h-44 max-w-60",
              )}
            />
          </button>
        ) : (
          <div key={file.id} className="flex max-w-80 items-center gap-0.5 rounded-xl border border-border/80 bg-card py-1 pl-1 pr-1 text-[12px]">
            {/* A text file or a PDF opens in a tab, as before; any other file — and one that is
                deleted once downloaded — is served as a download, so tapping it saves it. */}
            <a
              href={file.deleteAfterDownload ? api.agentAttachmentDownloadUrl(file.id) : api.agentAttachmentUrl(file.id)}
              {...(file.kind === "file" || file.deleteAfterDownload ? { download: file.filename } : { target: "_blank", rel: "noreferrer" })}
              title={file.kind === "file" || file.deleteAfterDownload ? `Download ${file.filename}` : file.filename}
              className="flex min-w-0 flex-1 items-center gap-2 rounded-lg px-1.5 py-0.5 hover:bg-muted/60"
            >
              <FileIcon filename={file.filename} className="size-4 shrink-0 text-primary" />
              <span className="min-w-0">
                <span className="block truncate font-medium">{file.filename}</span>
                <span className="block text-[10.5px] text-muted-foreground">
                  {fileTypeLabel(file.filename, file.kind)} · {humanBytes(file.bytes)}
                  {linkNote(file)}
                </span>
              </span>
            </a>
            <a
              href={api.agentAttachmentDownloadUrl(file.id)}
              download={file.filename}
              className={chipButton}
              aria-label={`Download ${file.filename}`}
              title={file.deleteAfterDownload ? "Download (the file is then deleted)" : "Download"}
            >
              <Download className="size-4" />
            </a>
            {file.url && !file.deleteAfterDownload ? (
              <button
                type="button"
                className={chipButton}
                onClick={() => copyFileLink(file.url!, toast)}
                aria-label={`Copy the link to ${file.filename}`}
                title={file.shared ? "Copy link (anyone with it can download)" : "Copy link (works when signed in to the board)"}
              >
                <Link2 className="size-4" />
              </button>
            ) : null}
            {shareButton(file, chipButton)}
            {deleteButton(file, chipButton)}
          </div>
        ),
      )}
    </div>
  );
}

/**
 * A burst of tool calls as one line of prose — "Ran 4 commands, edited 2 files" — closed until
 * tapped, running or not: the agent's words are the conversation, its `git status` output is not.
 * Open, it lists each call and a call opens to its input and result; a lone call skips the list
 * and opens straight to its detail.
 */
function ToolGroup({ messages }: { messages: AgentMessage[] }) {
  const [open, setOpen] = useState(false);
  const calls = messages.map(toolCallOf);
  const running = calls.some((call) => call.status === "running");
  const failed = calls.filter((call) => call.status === "error").length;
  const single = calls.length === 1 ? calls[0] : null;
  const detail = single ? callDetail(single) : "";
  return (
    <div className="text-[12px] leading-snug">
      <button
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex min-h-8 w-full min-w-0 items-center gap-2 text-left text-muted-foreground hover:text-foreground"
      >
        {running ? (
          <Loader2 className="size-3.5 shrink-0 animate-spin" style={{ color: "var(--kind-active)" }} />
        ) : failed ? (
          <AlertTriangle className="size-3.5 shrink-0 text-destructive" />
        ) : (
          <Check className="size-3.5 shrink-0 opacity-70" style={{ color: "var(--kind-done)" }} />
        )}
        {/* A lone call's summary is two words, so its detail is what gives way at 390px. */}
        <span className={detail ? "shrink-0" : "min-w-0 flex-1 truncate"}>{summarizeTools(calls)}</span>
        {detail ? <span className="min-w-0 flex-1 truncate font-mono text-[11.5px] text-foreground/70">{detail}</span> : null}
        {failed ? <span className="shrink-0 text-destructive">{single ? "failed" : `${failed} failed`}</span> : null}
        <ChevronRight className={cn("size-3.5 shrink-0 transition-transform", open && "rotate-90")} />
      </button>
      {open ? (
        single ? (
          <ToolDetail call={single} />
        ) : (
          <div className="ml-1.5 border-l border-border/60 pl-2.5">
            {messages.map((message) => (
              <ToolCallRow key={message.id} call={toolCallOf(message)} />
            ))}
          </div>
        )
      ) : null}
    </div>
  );
}

/** One call in an open group, the way Claude Code shows it: `⏺ Bash(git status)`. */
function ToolCallRow({ call }: { call: ToolCall }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="font-mono text-[11.5px] leading-snug">
      <button
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex min-h-8 w-full min-w-0 items-center gap-1.5 text-left text-muted-foreground hover:text-foreground"
      >
        <span
          className={cn("shrink-0", call.status === "running" && "animate-pulse")}
          style={{ color: call.status === "error" ? "var(--destructive)" : call.status === "done" ? "var(--kind-done)" : "var(--kind-active)" }}
        >
          ⏺
        </span>
        <span className="min-w-0 flex-1 truncate text-foreground/85">{call.body}</span>
        <ChevronRight className={cn("size-3 shrink-0 transition-transform", open && "rotate-90")} />
      </button>
      {open ? <ToolDetail call={call} /> : null}
    </div>
  );
}

/** A call's full input and result, each scrolling in its own box. */
function ToolDetail({ call }: { call: ToolCall }) {
  const result = (call.result ?? "").trim();
  return (
    <div className="mb-1 ml-4 mt-0.5 space-y-1.5 rounded-md border border-border/60 bg-muted/40 p-2 font-mono text-[11.5px] leading-snug">
      {call.input ? <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-all text-muted-foreground scrollbar-slim">{call.input}</pre> : null}
      {result ? (
        <pre className={cn("max-h-80 overflow-auto whitespace-pre-wrap break-all scrollbar-slim", call.status === "error" && "text-destructive")}>
          {result}
        </pre>
      ) : (
        <p className="text-muted-foreground">{call.status === "running" ? "running…" : "no output"}</p>
      )}
    </div>
  );
}
